import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { _saveAlertHistory } from '../../api/api';
import {OlangItem} from '../../../shared/Olang/user-interface/OlangItem/OlangItem';

const InterventionModal = ({ visible, onHide, alertHistory, onSuccess }) => {
  const [comment, setComment] = useState('');
  const [isRead, setIsRead] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);

  // Initialiser les valeurs quand le modal s'ouvre
  useEffect(() => {
    if (visible && alertHistory) {
      // Ne pas préremplir le commentaire - champ vide pour nouveau commentaire
      setComment('');
      setIsRead(Boolean(alertHistory.isRead));
    }
  }, [visible, alertHistory]);

  const handleSave = async () => {
    if (!alertHistory) return;

    console.log('🛠️ Intervention sur l\'alerte:', alertHistory.uid);
    setLoading(true);

    try {
      const data = {
        id: alertHistory.uid, // Utiliser uid comme id
        comment: comment.trim(),
        isRead: isRead ? 1 : 0
      };

      console.log('🛠️ Données d\'intervention:', data);

      const response = await _saveAlertHistory(data);

      if (response && response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Succès',
          detail: 'Intervention enregistrée avec succès',
          life: 3000
        });

        // Appeler la fonction de succès pour rafraîchir la liste
        if (onSuccess) {
          onSuccess();
        }

        // Fermer le modal
        onHide();
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'intervention:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Erreur lors de l\'enregistrement de l\'intervention',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Réinitialiser les valeurs
    setComment('');
    setIsRead(false);
    onHide();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button 
        label="Annuler" 
        icon="pi pi-times" 
        onClick={handleCancel}
        className="p-button-outlined"
        disabled={loading}
      />
      <Button 
        label="Enregistrer" 
        icon="pi pi-check" 
        onClick={handleSave}
        loading={loading}
        className="p-button-success"
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Intervention sur l'Alerte"
        visible={visible}
        onHide={handleCancel}
        footer={footer}
        style={{ width: '600px' }}
        modal
        className="p-fluid"
      >
        {alertHistory && (
          <div className="flex flex-column gap-4">
            {/* Informations de l'alerte */}
            <div className="p-3 border-round surface-100">
              <h4 className="mt-0 mb-2">Informations de l'Alerte</h4>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <strong>Code:</strong> {alertHistory.alertCode}
                </div>
                <div className="col-12 md:col-6">
                  <strong>Type:</strong> {alertHistory.sensorType}
                </div>
                <div className="col-12">
                  <strong>Message:</strong> {alertHistory.message}
                </div>
                <div className="col-12 md:col-6">
                  <strong>Capteur:</strong> {alertHistory.macAddr}
                </div>
                <div className="col-12 md:col-6">
                  <strong>Condition:</strong> {alertHistory.condition}
                </div>
              </div>
            </div>

            {/* Historique des commentaires existants */}
            {(() => {
              // Parser le JSON des commentaires
              let parsedComments = [];
              try {
                if (alertHistory.comments && typeof alertHistory.comments === 'string') {
                  parsedComments = JSON.parse(alertHistory.comments);
                } else if (Array.isArray(alertHistory.comments)) {
                  parsedComments = alertHistory.comments;
                }
              } catch (error) {
                console.error('Erreur lors du parsing des commentaires:', error);
                parsedComments = [];
              }
              
              // Afficher la section des commentaires si disponible
              return parsedComments && parsedComments.length > 0 && (
                <div className="field">
                  <label className="font-semibold mb-2 block">
                    Historique des Interventions ({parsedComments.length})
                  </label>
                  <div className="p-3 border-round surface-50 max-h-20rem overflow-y-auto">
                    {parsedComments.map((commentItem, index) => (
                      <div key={commentItem.id || index} className="mb-3 p-2 border-round surface-0 border-1 surface-border">
                        <div className="flex justify-content-between align-items-start mb-2">
                          <span className="text-sm text-500 font-medium">
                            {commentItem.date ? new Date(commentItem.date).toLocaleString('fr-FR') : 'Date non disponible'}
                          </span>
                          {commentItem.id && (
                            <span className="text-xs text-400 font-medium">
                              ID: {commentItem.id}
                            </span>
                          )}
                        </div>
                        <p className="m-0 text-900 line-height-3">
                          {commentItem.message || commentItem.comment || commentItem.text || 'Commentaire vide'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Nouveau commentaire */}
            <div className="field">
              <label htmlFor="comment" className="font-semibold">
                Nouveau Commentaire d'Intervention
              </label>
              <InputTextarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder="Saisir un nouveau commentaire sur l'intervention..."
                className="w-full"
              />
            </div>

            {/* Statut de traitement */}
            <div className="field">
              <div className="flex align-items-center gap-3">
                <label htmlFor="isRead" className="font-semibold">
                  Marquer comme traité
                </label>
                <InputSwitch
                  id="isRead"
                  checked={isRead}
                  onChange={(e) => setIsRead(e.value)}
                />
                <span className="text-sm text-500">
                  {isRead ? 'Alerte marquée comme traitée' : 'Alerte non traitée'}
                </span>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default InterventionModal;
